import { useCallback } from 'react';

import { stripDiacritics } from '@realadvisor/transform/strip-diacritics';

import {
  type Activities,
  type ActivityItemFragment,
  Activity_Types_Enum_Enum,
  type Leads,
} from '@graphql/__generated__/graphql';
import { ActivitySortOrder } from '@modules/_shared/components/feed-v2/components/ActivityFilters/ActivitySortSelect';
import type { ActivityGroup } from '@modules/_shared/components/feed-v2/types';
import { useLocale } from '@shared/hooks/locale';

// Check if an assignment activity is an appraisal request
// (when lead source is 'appraisal' and activity was created by the same person who created the lead)
export const isAppraisalRequest = (
  activity: Pick<Activities, 'created_by' | 'activity_type'> & {
    lead?: (Pick<Leads, 'created_by'> & { source: { name: string } }) | null;
  },
) =>
  activity.activity_type === 'assignment' &&
  activity.lead?.source?.name === 'appraisal' &&
  activity.created_by === activity.lead?.created_by;

const isProspectionMapExtra = (extraData: unknown): boolean =>
  extraData != null &&
  typeof extraData === 'object' &&
  'source' in extraData &&
  extraData.source === 'prospection_map';

export const isProspectionMapOutreach = (
  activity: Pick<Activities, 'activity_type' | 'extra_data'>,
) =>
  activity.activity_type === 'email' &&
  isProspectionMapExtra(activity.extra_data);

export const isProspectionMapAssignment = (
  activity: Pick<Activities, 'activity_type' | 'extra_data'> & {
    assignee?: { id?: string | null } | null;
    lead?: {
      lead_agents?: Array<{
        user_id?: string | null;
        source?: string | null;
      }> | null;
    } | null;
  },
) => {
  if (activity.activity_type !== 'assignment') {
    return false;
  }
  if (isProspectionMapExtra(activity.extra_data)) {
    return true;
  }
  const assigneeId = activity.assignee?.id;
  return (
    assigneeId != null &&
    (activity.lead?.lead_agents ?? []).some(
      agent =>
        agent.user_id === assigneeId && agent.source === 'prospection_map',
    )
  );
};

// Check if two activities are "the same" for grouping purposes
const areActivitiesSame = (
  a: ActivityItemFragment | null | undefined,
  b: ActivityItemFragment | null | undefined,
) => {
  if (!a || !b) {
    return false;
  }

  // Must be same activity type
  if (a.activity_type !== b.activity_type) {
    return false;
  }

  // For notes, emails, and lot `change` activities, never group — each row
  // carries unique content (note body / email thread / extra_data describing
  // the specific field that changed). Without an extra_data equality check
  // a status change and a portal publish on the same lot by the same user
  // would otherwise collapse into a single "2x" row.
  if (
    [
      Activity_Types_Enum_Enum.Note,
      Activity_Types_Enum_Enum.Email,
      Activity_Types_Enum_Enum.Change,
      // Each stage change has a unique from/to pair — never collapse into "2x".
      Activity_Types_Enum_Enum.LeadStatusAgent,
      Activity_Types_Enum_Enum.ModuleGranted,
      Activity_Types_Enum_Enum.ModuleRevoked,
      // The title reads extra_data (submitted / approved, published /
      // unpublished), so a publish then an unpublish must stay two rows.
      Activity_Types_Enum_Enum.OnboardingStatusChanged,
      Activity_Types_Enum_Enum.AgentProfileVisibilityChanged,
    ].includes(a.activity_type)
  ) {
    return false;
  }

  // Must have same creator (compare IDs, treat null/undefined as same)
  if ((a.creator?.id ?? null) !== (b.creator?.id ?? null)) {
    return false;
  }

  // Must have same assignee - EXCEPT for calls where assignee can change on outcome
  if (a.activity_type !== 'call') {
    if ((a.assignee?.id ?? null) !== (b.assignee?.id ?? null)) {
      return false;
    }
  }

  // Don't group if linked to different leads
  if ((a.lead_id ?? null) !== (b.lead_id ?? null)) {
    return false;
  }

  // Must have same status (normalize: treat null/undefined/false as false, true as true)
  const aDone = a.done === true;
  const bDone = b.done === true;
  const aSuccess = a.success === true;
  const bSuccess = b.success === true;

  if (aDone !== bDone) {
    return false;
  }
  if (aSuccess !== bSuccess) {
    return false;
  }

  // Don't group if either has a meaningful note - show activities with notes separately
  // Treat null, undefined, empty string, whitespace-only as "no note"
  const aHasNote = a.note && a.note.trim().length > 0;
  const bHasNote = b.note && b.note.trim().length > 0;
  if (aHasNote || bHasNote) {
    return false;
  }

  // For tasks, also compare subject
  if (a.activity_type === 'task' && a.subject !== b.subject) {
    return false;
  }

  return true;
};

// Group consecutive identical activities
export const groupActivities = (
  activities: ActivityItemFragment[],
): ActivityGroup[] => {
  const groups: ActivityGroup[] = [];

  for (const activity of activities) {
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && areActivitiesSame(lastGroup.activities[0], activity)) {
      lastGroup.activities.push(activity);
    } else {
      // Create new group with at least one activity (tuple type)
      groups.push({ activities: [activity] });
    }
  }

  return groups;
};

// --- Types ---

export type ActivityFeedFilters = {
  selectedTypes: Activity_Types_Enum_Enum[];
  selectedParticipants: string[];
  leadFilter: string;
  searchTerm: string;
};

export type SearchableActivity<
  T extends ActivityItemFragment = ActivityItemFragment,
> = {
  activity: T;
  searchText: string;
};

const PENDING_ACTION_TYPES = ['task', 'call', 'visit', 'assignment'];

export const sortByCreatedDate = (
  a: ActivityItemFragment,
  b: ActivityItemFragment,
) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

const sortByPendingFirst = (
  a: ActivityItemFragment,
  b: ActivityItemFragment,
) => {
  const aIsPending = PENDING_ACTION_TYPES.includes(a.activity_type) && !a.done;
  const bIsPending = PENDING_ACTION_TYPES.includes(b.activity_type) && !b.done;

  if (aIsPending !== bIsPending) {
    return aIsPending ? -1 : 1;
  }
  return sortByCreatedDate(a, b);
};

const sortByDoneDate = (a: ActivityItemFragment, b: ActivityItemFragment) => {
  const aIsDone = a.done === true;
  const bIsDone = b.done === true;

  if (aIsDone !== bIsDone) {
    return aIsDone ? -1 : 1;
  }

  if (aIsDone && bIsDone) {
    const aDoneAt = new Date(a.consolidated_done_at ?? a.created_at);
    const bDoneAt = new Date(b.consolidated_done_at ?? b.created_at);
    return bDoneAt.getTime() - aDoneAt.getTime();
  }

  return sortByCreatedDate(a, b);
};

export const sortStrategies: Record<
  ActivitySortOrder,
  (a: ActivityItemFragment, b: ActivityItemFragment) => number
> = {
  [ActivitySortOrder.PendingFirst]: sortByPendingFirst,
  [ActivitySortOrder.DoneDesc]: sortByDoneDate,
  [ActivitySortOrder.CreatedDesc]: sortByCreatedDate,
};

export const buildSearchableActivities = <T extends ActivityItemFragment>(
  activities: T[],
  translateFn: (type: Activity_Types_Enum_Enum) => string,
): SearchableActivity<T>[] =>
  activities.map(activity => ({
    activity,
    searchText: stripDiacritics(
      [
        activity.creator?.full_name,
        activity.assignee?.full_name,
        activity.lead?.contact?.full_name,
        activity.lead?.property?.route,
        activity.lead?.property?.street_number,
        activity.lead?.property?.locality,
        activity.lead?.property?.postcode,
        activity.note,
        activity.subject,
        activity.email_message?.subject,
        activity.email_message?.snippet,
        activity.activity_type,
        translateFn(activity.activity_type),
      ]
        .filter(Boolean)
        .join(' '),
    ),
  }));

export const filterActivities = <T extends ActivityItemFragment>(
  items: SearchableActivity<T>[],
  filters: ActivityFeedFilters,
  leadId?: string,
): T[] => {
  const searchFilterRegex =
    filters.searchTerm.replaceAll(' ', '').length === 0
      ? null
      : new RegExp(
          stripDiacritics(filters.searchTerm).replace(
            /[.*+?^${}()|[\]\\]/g,
            '\\$&',
          ),
          'ig',
        );

  return items.reduce<T[]>((acc, { activity, searchText }) => {
    let isValid = true;

    isValid &&=
      searchFilterRegex == null || searchText.match(searchFilterRegex) != null;
    isValid &&=
      filters.selectedTypes.length === 0 ||
      filters.selectedTypes.includes(activity.activity_type);
    isValid &&=
      filters.selectedParticipants.length === 0 ||
      filters.selectedParticipants.includes(activity.assignee?.id ?? '') ||
      filters.selectedParticipants.includes(activity.lead?.contact?.id ?? '') ||
      // `change` activities have no assignee/contact — match by creator
      // (mirrors the participant collection in ActivityFilters).
      (activity.activity_type === 'change' &&
        filters.selectedParticipants.includes(activity.creator?.id ?? ''));

    if (leadId != null) {
      isValid &&=
        filters.leadFilter === 'all' ||
        activity.lead_id == null ||
        leadId === activity.lead_id;
    }

    if (isValid) {
      acc.push(activity);
    }

    return acc;
  }, []);
};

export const useTranslateActivityType = () => {
  const { t } = useLocale();

  return useCallback(
    (type: Activity_Types_Enum_Enum) => {
      switch (type) {
        case Activity_Types_Enum_Enum.Assignment:
          return t('Assignment');
        case Activity_Types_Enum_Enum.Requalification:
          return t('Requalification');
        case Activity_Types_Enum_Enum.Note:
          return t('Note');
        case Activity_Types_Enum_Enum.Origination:
          return t('New lead');
        case Activity_Types_Enum_Enum.Visit:
          return t('Visit');
        case Activity_Types_Enum_Enum.Call:
          return t('Call');
        case Activity_Types_Enum_Enum.Task:
          return t('Task');
        case Activity_Types_Enum_Enum.Email:
          return t('Email');
        case Activity_Types_Enum_Enum.CreateUser:
          return t('New user');
        case Activity_Types_Enum_Enum.Match:
          return t('Matches');
        case Activity_Types_Enum_Enum.Offer:
          return t('Offer');
        case Activity_Types_Enum_Enum.AgentOptIn:
          return t('Agent funnel opt-in');
        case Activity_Types_Enum_Enum.AgentQuizCompleted:
          return t('Agent quiz completed');
        case Activity_Types_Enum_Enum.Change:
          return t('Listing update');
        case Activity_Types_Enum_Enum.LeadStatusAgent:
          return t('Stage change');
        case Activity_Types_Enum_Enum.ModuleGranted:
          return t('Module granted');
        case Activity_Types_Enum_Enum.ModuleRevoked:
          return t('Module removed');
        default:
          return type;
      }
    },
    [t],
  );
};
